import { format, isPast } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  AlertTriangle,
  Calendar as CalendarIcon,
  CalendarClock,
  ChevronDown,
  Clock,
  ExternalLink,
  Globe,
  Loader2,
  Plus,
  Search,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  useCancelMeetingMutation,
  useGetFreeSlotsQuery,
  useListMeetingsQuery,
  useScheduleMeetingMutation,
} from '@/services/api/mattersService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import { User } from '@/services/types/user-types';
import { selectUser } from '@/state/selectors/root-selectors';
import { getIanaTimezone, getTimezoneAbbrev } from '@/utils/timezone';

interface Meeting {
  id: string;
  scheduled_at: string | null;
  scheduled_by_id: string | null;
  scheduled_by_name: string | null;
  attendee_ids: string[];
  attendee_names: string[];
  notes: string | null;
  google_calendar_event_id: string | null;
  google_calendar_event_link: string | null;
  cancelled_at: string | null;
  created_at: string | null;
}

interface ScheduleMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  matterName?: string;
  clientState?: string | null;
  /** Freetext location like "Los Angeles, CA" — used as fallback when clientState is null */
  clientLocation?: string | null;
}

const displayUserName = (user: User | null): string => {
  if (!user) return '';
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.first_name || user.username;
};

/** Parse "HH:MM" to a 12-hour display string */
function formatTime24to12(time24: string): string {
  const [hStr, mStr] = time24.split(':');
  const h = parseInt(hStr, 10);
  const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${hour12}:${mStr} ${ampm}`;
}

/** Extract a US state abbreviation from a freetext location like "Los Angeles, CA" */
function extractStateFromLocation(location: string | null | undefined): string | null {
  if (!location) return null;
  // Match ", XX" at end or standalone 2-letter state code
  const match = location.match(/,\s*([A-Z]{2})\s*$/);
  return match ? match[1] : null;
}

export const ScheduleMeetingModal: React.FC<ScheduleMeetingModalProps> = ({
  isOpen,
  onClose,
  matterId,
  matterName,
  clientState: clientStateProp,
  clientLocation,
}) => {
  // Resolve client state: use explicit prop, or extract from freetext location
  const clientState = clientStateProp || extractStateFromLocation(clientLocation);

  const [scheduleMeeting] = useScheduleMeetingMutation();
  const [cancelMeeting] = useCancelMeetingMutation();
  const { data: meetings = [], refetch } = useListMeetingsQuery(matterId, {
    skip: !isOpen,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [calendarError, setCalendarError] = useState(false);
  const [showPastMeetings, setShowPastMeetings] = useState(false);

  // Form state
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [manualTimeInput, setManualTimeInput] = useState('');
  const [selectedAttendeeIds, setSelectedAttendeeIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const { data: staffUsers = [], isLoading: staffLoading } = useGetStaffUsersQuery();
  const currentUser = useSelector(selectUser);
  const hasSeededRef = useRef(false);

  // Client timezone
  const clientTzIana = useMemo(
    () => (clientState ? getIanaTimezone(clientState) : undefined),
    [clientState],
  );
  const clientTzAbbrev = clientState
    ? getTimezoneAbbrev(clientState) || 'ET'
    : 'ET';

  // User's local timezone
  const userTzIana = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );
  const userTzAbbrev = useMemo(() => {
    const short = new Intl.DateTimeFormat('en-US', {
      timeZone: userTzIana,
      timeZoneName: 'short',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return short?.value || 'Local';
  }, [userTzIana]);

  // Free slots query
  const freeSlotsDate = selectedDate
    ? format(selectedDate, 'yyyy-MM-dd')
    : '';
  const { data: freeSlots = [], isFetching: slotsFetching } =
    useGetFreeSlotsQuery(
      { matterId, date: freeSlotsDate, timezone: userTzIana },
      { skip: !isOpen || !selectedDate || !showForm },
    );

  // Parse free slots into simple HH:MM values, excluding past slots.
  // Show empty while fetching to avoid stale data from a previous date.
  const parsedFreeSlots = useMemo(() => {
    if (slotsFetching) return [];
    const now = new Date();
    return freeSlots
      .filter((slot) => new Date(slot.start_time) > now)
      .map((slot) => {
        const d = new Date(slot.start_time);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      });
  }, [freeSlots, slotsFetching]);

  const sortedUsers = useMemo(() => {
    return [...staffUsers].sort((a, b) => {
      const aName = `${a.first_name} ${a.last_name}`.trim() || a.username;
      const bName = `${b.first_name} ${b.last_name}`.trim() || b.username;
      return aName.localeCompare(bName);
    });
  }, [staffUsers]);

  // Split meetings into active (upcoming/today) and past
  const { activeMeetings, pastMeetings } = useMemo(() => {
    return meetings.reduce<{
      activeMeetings: Meeting[];
      pastMeetings: Meeting[];
    }>(
      (acc, meeting) => {
        const belongsInPast =
          Boolean(meeting.cancelled_at) ||
          Boolean(meeting.scheduled_at && isPast(new Date(meeting.scheduled_at)));

        if (belongsInPast) {
          acc.pastMeetings.push(meeting);
        } else {
          acc.activeMeetings.push(meeting);
        }

        return acc;
      },
      { activeMeetings: [], pastMeetings: [] },
    );
  }, [meetings]);

  // Reset form state when opening
  useEffect(() => {
    if (isOpen) {
      setShowForm(false);
      setCalendarError(false);
      setShowPastMeetings(false);
      hasSeededRef.current = false;
    }
  }, [isOpen]);

  // Seed current user into attendees when starting a new form
  useEffect(() => {
    if (showForm && currentUser?.id && !hasSeededRef.current) {
      hasSeededRef.current = true;
      setSelectedAttendeeIds([currentUser.id]);
    }
  }, [showForm, currentUser?.id]);

  const openNewForm = () => {
    setSelectedDate(null);
    setSelectedTime('');
    setManualTimeInput('');
    setSelectedAttendeeIds(currentUser?.id ? [currentUser.id] : []);
    setNotes('');
    setCalendarError(false);
    hasSeededRef.current = true;
    setShowForm(true);
  };

  const canSubmit =
    selectedDate && selectedTime && selectedAttendeeIds.length > 0 && !isSubmitting;

  const handleSubmit = async (skipCalendar = false) => {
    if (!selectedDate || !selectedTime || selectedAttendeeIds.length === 0) return;

    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const isoString = `${year}-${month}-${day}T${selectedTime}:00`;

    setIsSubmitting(true);
    setCalendarError(false);
    try {
      await scheduleMeeting({
        matterId,
        scheduled_at: isoString,
        attendee_ids: selectedAttendeeIds,
        notes: notes || undefined,
        skip_calendar: skipCalendar || undefined,
        timezone: userTzIana,
      }).unwrap();
      setShowForm(false);
      refetch();
    } catch (err: unknown) {
      const apiError = err as {
        status?: number;
        data?: { calendar_failed?: boolean };
      };
      if (apiError?.status === 422 && apiError?.data?.calendar_failed) {
        setCalendarError(true);
      } else {
        setCalendarError(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelMeeting = async (meetingId: string) => {
    setIsSubmitting(true);
    try {
      await cancelMeeting({ matterId, meetingId }).unwrap();
      refetch();
    } catch {
      // Error handled
    } finally {
      setIsSubmitting(false);
    }
  };

  const addAttendee = (user: User | null) => {
    if (user && !selectedAttendeeIds.includes(user.id)) {
      setSelectedAttendeeIds((prev) => [...prev, user.id]);
    }
  };

  const removeAttendee = (userId: string) => {
    setSelectedAttendeeIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleManualTimeSubmit = () => {
    const trimmed = manualTimeInput.trim();
    // Accept HH:MM or H:MM format
    const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      const h = parseInt(match[1], 10);
      const m = parseInt(match[2], 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        setSelectedTime(val);
        setManualTimeInput('');
        return;
      }
    }
    // Try 12-hour format like "2:30 PM"
    const match12 = trimmed.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (match12) {
      let h = parseInt(match12[1], 10);
      const m = parseInt(match12[2], 10);
      const isPM = match12[3].toLowerCase() === 'pm';
      if (isPM && h < 12) h += 12;
      if (!isPM && h === 12) h = 0;
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        setSelectedTime(val);
        setManualTimeInput('');
      }
    }
  };

  /** Render a single meeting card */
  const renderMeetingCard = (meeting: Meeting, isPastMeeting: boolean) => {
    if (!meeting.scheduled_at) return null;
    const scheduledDate = new Date(meeting.scheduled_at);
    const isCancelled = !!meeting.cancelled_at;

    return (
      <div
        key={meeting.id}
        className={cn(
          'rounded-lg border px-3 py-2.5 space-y-1.5',
          isCancelled
            ? 'bg-zinc-50 border-zinc-200 opacity-60'
            : isPastMeeting
              ? 'bg-zinc-50 border-zinc-200'
              : 'bg-white border-zinc-200',
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-sm font-bold text-zinc-900">
                {format(scheduledDate, 'MMM d, yyyy')}
              </span>
              <span className="text-zinc-500 text-sm font-medium">at</span>
              <span className="text-sm font-bold text-zinc-900">
                {format(scheduledDate, 'h:mm a')}
              </span>
              <span className="text-[11px] font-bold text-zinc-500">{userTzAbbrev}</span>
            </div>
            {clientTzIana && clientTzIana !== userTzIana && (
              <div className="text-[12px] font-medium text-zinc-500 mt-0.5">
                {formatInTimeZone(scheduledDate, clientTzIana, 'h:mm a')}{' '}
                <span className="font-bold">{clientTzAbbrev}</span> client time
              </div>
            )}
          </div>
          {isCancelled && (
            <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider bg-red-50 px-1.5 py-0.5 rounded">
              Cancelled
            </span>
          )}
        </div>

        {meeting.scheduled_by_name && (
          <div className="text-[12px] text-zinc-500 font-medium">
            Booked by <span className="font-bold text-zinc-700">{meeting.scheduled_by_name}</span>
          </div>
        )}

        {meeting.attendee_names.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {meeting.attendee_names.map((name, i) => (
              <span
                key={meeting.attendee_ids[i]}
                className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-bold text-zinc-700"
              >
                {name}
              </span>
            ))}
          </div>
        )}

        {meeting.notes && (
          <p className="text-xs text-zinc-600 font-medium">{meeting.notes}</p>
        )}

        <div className="flex items-center gap-2">
          {meeting.google_calendar_event_link && (
            <a
              href={meeting.google_calendar_event_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              Calendar
            </a>
          )}
          {!isCancelled && !isPastMeeting && (
            <button
              type="button"
              onClick={() => handleCancelMeeting(meeting.id)}
              disabled={isSubmitting}
              className="text-[11px] font-bold text-red-500 hover:text-red-600 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderNewMeetingForm = () => (
    <div className="space-y-3 border-t border-zinc-100 pt-3">
      <div className="text-sm font-bold text-zinc-900">New Meeting</div>

      {/* Calendar error */}
      {calendarError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-800">
              <p className="font-bold">Calendar event could not be created.</p>
              <p className="mt-0.5 text-amber-700">Schedule without calendar, or retry.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleSubmit(true)}
              disabled={isSubmitting}
              className="h-7 px-3 rounded-full text-xs font-bold bg-amber-600 text-white hover:bg-amber-700 active:scale-95 transition-all disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Skip Calendar'}
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={isSubmitting}
              className="h-7 px-3 rounded-full text-xs font-bold text-amber-700 hover:bg-amber-100 active:scale-95 transition-all disabled:opacity-50"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Split pane: Left = calendar + form, Right = free slots */}
      <div className="flex gap-4">
        {/* Left pane: Date picker */}
        <div className="flex-shrink-0">
          <Calendar
            mode="single"
            selected={selectedDate || undefined}
            onSelect={(d) => {
              setSelectedDate(d || null);
              setSelectedTime('');
            }}
            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
            className="rounded-lg border border-zinc-200"
          />
        </div>

        {/* Right pane: Time slots */}
        <div className="flex-1 min-w-0">
          {!selectedDate ? (
            <div className="h-full flex items-center justify-center text-sm text-zinc-400 font-medium">
              <div className="text-center space-y-1">
                <CalendarIcon className="h-8 w-8 mx-auto text-zinc-300" />
                <p>Pick a date to see available times</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-zinc-700">
                  {format(selectedDate, 'EEEE, MMM d')}
                  <span className="text-zinc-400 font-medium ml-1">({userTzAbbrev})</span>
                </div>
                {slotsFetching && (
                  <Loader2 className="h-3 w-3 animate-spin text-zinc-400" />
                )}
              </div>

              {/* Selected time display */}
              {selectedTime && selectedDate && (
                <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2 space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-primary" />
                    <span className="text-sm font-bold text-primary">
                      {formatTime24to12(selectedTime)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedTime('')}
                      className="ml-auto text-primary/60 hover:text-primary"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  {clientTzIana && (
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-500 pl-5">
                      <Globe className="h-3 w-3 text-zinc-400 shrink-0" />
                      {(() => {
                        const year = selectedDate.getFullYear();
                        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                        const day = String(selectedDate.getDate()).padStart(2, '0');
                        const localDate = new Date(`${year}-${month}-${day}T${selectedTime}:00`);
                        return formatInTimeZone(localDate, clientTzIana, 'h:mm a');
                      })()}{' '}
                      <span className="font-bold">{clientTzAbbrev}</span> client time
                    </div>
                  )}
                </div>
              )}

              {/* Manual time input */}
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={manualTimeInput}
                  onChange={(e) => setManualTimeInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualTimeSubmit()}
                  placeholder="Custom time (e.g. 2:30 PM)"
                  className="flex-1 h-8 px-2.5 rounded-lg border border-zinc-200 text-xs font-medium text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <button
                  type="button"
                  onClick={handleManualTimeSubmit}
                  disabled={!manualTimeInput.trim()}
                  className="h-8 px-2.5 rounded-lg text-xs font-bold text-primary hover:bg-primary/5 disabled:opacity-30 transition-all"
                >
                  Set
                </button>
              </div>

              {/* Free slot grid */}
              {/* Loading skeleton */}
              {slotsFetching && (
                <div>
                  <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
                    Checking availability...
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={i}
                        className="h-8 rounded-lg bg-zinc-100 animate-pulse"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Available slots */}
              {!slotsFetching && parsedFreeSlots.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">
                    Available slots
                  </div>
                  <div className="grid grid-cols-2 gap-1 max-h-[200px] overflow-y-auto pr-1">
                    {parsedFreeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setSelectedTime(time)}
                        className={cn(
                          'h-8 rounded-lg text-xs font-bold transition-all',
                          selectedTime === time
                            ? 'bg-primary text-white shadow-sm'
                            : 'bg-zinc-50 text-zinc-700 border border-zinc-200 hover:border-primary hover:text-primary',
                        )}
                      >
                        {formatTime24to12(time)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!slotsFetching && parsedFreeSlots.length === 0 && (
                <div className="text-xs text-zinc-400 font-medium text-center py-2">
                  No free slots found — use manual input above
                </div>
              )}

            </div>
          )}
        </div>
      </div>

      {/* Attendees */}
      <div className="space-y-1">
        <div className="text-xs font-bold text-zinc-700">Attendees</div>
        {selectedAttendeeIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {selectedAttendeeIds.map((id) => {
              const user = staffUsers.find((u) => u.id === id);
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/5 px-2.5 py-1 text-xs font-bold text-primary border border-primary/20"
                >
                  {user ? displayUserName(user) : id}
                  <button
                    type="button"
                    onClick={() => removeAttendee(id)}
                    className="hover:text-red-500 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <AttendeeSearch
          users={sortedUsers}
          selectedIds={selectedAttendeeIds}
          onAdd={addAttendee}
          disabled={staffLoading || isSubmitting}
        />
      </div>

      {/* Notes */}
      <div className="space-y-1">
        <div className="text-xs font-bold text-zinc-700">Notes (optional)</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isSubmitting}
          placeholder="Meeting agenda, discussion points..."
          rows={2}
          className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm font-medium text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-200 resize-none"
        />
      </div>

      {/* Submit */}
      {!calendarError && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleSubmit(false)}
            disabled={!canSubmit}
            className={cn(
              'h-9 px-4 rounded-lg text-sm font-bold transition-all flex items-center gap-1.5',
              canSubmit
                ? 'bg-primary text-white hover:bg-primary/90 active:scale-95 shadow-sm'
                : 'bg-zinc-100 text-zinc-400 cursor-not-allowed',
            )}
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CalendarClock className="h-3.5 w-3.5" />
            )}
            {isSubmitting ? 'Scheduling...' : 'Schedule Meeting'}
          </button>
          <button
            type="button"
            onClick={() => setShowForm(false)}
            disabled={isSubmitting}
            className="h-9 px-3 rounded-lg text-sm font-bold text-zinc-500 hover:bg-zinc-100 active:scale-95 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(
        'max-h-[85vh] overflow-y-auto p-4',
        showForm ? 'sm:max-w-2xl' : 'sm:max-w-lg',
      )}>
        <DialogHeader className="pb-0">
          <DialogTitle className="flex items-center gap-2 text-base font-bold text-zinc-900">
            <CalendarClock className="h-4 w-4 text-primary" />
            Meetings
            {matterName && (
              <span className="text-sm font-medium text-zinc-500 ml-1">— {matterName}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-2.5">
          {/* Upcoming meetings */}
          {activeMeetings.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">
                Upcoming
              </div>
              {activeMeetings.map((m) => renderMeetingCard(m, false))}
            </div>
          )}

          {/* New meeting button or form */}
          {!showForm ? (
            <button
              type="button"
              onClick={openNewForm}
              className="w-full h-8 rounded-lg border-2 border-dashed border-zinc-200 text-xs font-bold text-zinc-500 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Schedule New Meeting
            </button>
          ) : (
            renderNewMeetingForm()
          )}

          {/* Past / cancelled meetings */}
          {pastMeetings.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowPastMeetings(!showPastMeetings)}
                className="flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-700 transition-colors"
              >
                <ChevronDown
                  className={cn(
                    'h-3 w-3 transition-transform',
                    !showPastMeetings && '-rotate-90',
                  )}
                />
                Past & Cancelled ({pastMeetings.length})
              </button>
              {showPastMeetings && (
                <div className="space-y-2 mt-2">
                  {pastMeetings.map((m) => renderMeetingCard(m, true))}
                </div>
              )}
            </div>
          )}

          {activeMeetings.length === 0 && pastMeetings.length === 0 && !showForm && (
            <div className="text-center py-3 text-sm font-medium text-zinc-500">
              No meetings scheduled yet
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Simple searchable user picker */
const AttendeeSearch: React.FC<{
  users: User[];
  selectedIds: string[];
  onAdd: (user: User | null) => void;
  disabled?: boolean;
}> = ({ users, selectedIds, onAdd, disabled }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filtered = useMemo(() => {
    const available = users.filter((u) => !selectedIds.includes(u.id));
    if (!query.trim()) return available.slice(0, 20);
    const q = query.toLowerCase();
    return available
      .filter((u) => {
        const name = `${u.first_name} ${u.last_name}`.trim().toLowerCase();
        return name.includes(q) || u.username.toLowerCase().includes(q);
      })
      .slice(0, 20);
  }, [users, selectedIds, query]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="w-full h-9 px-3 rounded-lg border border-zinc-200 text-sm text-zinc-500 font-medium text-left flex items-center gap-2 hover:border-zinc-400 transition-all bg-zinc-50 disabled:opacity-50"
        >
          <Search className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
          Add attendee...
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-2 border-b border-zinc-100">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name..."
            className="w-full text-sm px-2.5 py-1.5 rounded-md border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-primary/20 font-medium"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>
        <div className="max-h-48 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-zinc-500 font-medium">
              No users found
            </div>
          ) : (
            filtered.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => {
                  onAdd(user);
                  setQuery('');
                }}
                className="w-full px-3 py-2 text-left text-sm font-medium text-zinc-800 hover:bg-zinc-50 transition-colors"
              >
                {displayUserName(user)}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
